import { KPI } from '@/components/ui/KPI';
import { SectionHead } from '@/components/ui/SectionHead';
import { SegmentTable } from '@/components/SegmentTable';
import type { OverviewSection, OverviewSegment } from '@/api/worldCup';
import type { DashboardConfig } from '../config';

type Props = {
  section: OverviewSection;
  hide?: DashboardConfig['hide'];
  /** Per-brand performance rows — when present, render the SegmentTable below the KPIs. */
  segments?: OverviewSegment[];
  /** Brand id → color (used by the SegmentTable when shown). */
  brandColors?: Record<string, string>;
};

export function OverviewView({ section, hide = {}, segments, brandColors = {} }: Props) {
  const { business_kpis: biz, engagement_kpis: eng } = section;

  // Each business tile only shows when (a) not hidden by config and
  // (b) the backend actually ships the metric — Spain omits revenue / avg_basket
  // and also hides Users / Traffic, leaving the whole section empty.
  const businessTiles = [
    !hide.revenue && biz.revenue && (
      <KPI key="rev" label="Revenue" value={biz.revenue.display} category="Business" accent="biz" foot={biz.revenue.foot ?? 'Campaign total'} />
    ),
    !hide.usersTraffic && (
      <KPI key="users" label="Users / Traffic" value={biz.users_traffic.display} category="Business" accent="biz" foot={biz.users_traffic.foot} />
    ),
    !hide.avgBasket && biz.avg_basket && (
      <KPI key="basket" label="Avg basket" value={biz.avg_basket.display} category="Business" accent="biz" foot={biz.avg_basket.foot} />
    ),
  ].filter(Boolean);

  // Pick a grid class that fits the visible count so single tiles don't sit in
  // a 3-column row with two phantom holes.
  const gridClass = businessTiles.length >= 3 ? 'grid-3' : businessTiles.length === 2 ? 'grid-2' : 'grid-1';

  return (
    <div>
      {businessTiles.length > 0 && (
        <>
          <SectionHead tag="business">Business KPIs</SectionHead>
          <div className={gridClass}>{businessTiles}</div>
        </>
      )}

      {/* Labels come from the payload when it sends one: the reward noun is
          campaign-specific ("Menus earned" for Coca-Cola Quick), which the
          pipeline knows and the UI shouldn't branch on. */}
      <SectionHead tag="user engagement">Engagement KPIs</SectionHead>
      <div className="grid-2">
        <KPI
          label={eng.unique_optins.label ?? 'Unique opt-ins'}
          value={eng.unique_optins.display}
          category="Engagement"
          accent="engage"
          foot={eng.unique_optins.foot}
        />
        <KPI
          label={eng.unique_visitors.label ?? 'Unique visitors'}
          value={eng.unique_visitors.display}
          category="Engagement"
          accent="engage"
          foot={eng.unique_visitors.foot}
        />
        {eng.avg_daily_visitors && (
          <KPI
            label={eng.avg_daily_visitors.label ?? 'Avg daily unique visitors'}
            value={eng.avg_daily_visitors.display}
            category="Engagement"
            accent="engage"
            foot={eng.avg_daily_visitors.foot}
          />
        )}
        {!hide.gamesPlayed && (
          <KPI
            label={eng.games_played.label ?? 'Games played'}
            value={eng.games_played.display}
            category="Engagement"
            accent="engage"
            foot={eng.games_played.foot}
          />
        )}
        <KPI
          label={eng.rewards_awarded.label ?? 'Rewards awarded'}
          value={eng.rewards_awarded.display}
          category="Engagement"
          accent="engage"
          foot={eng.rewards_awarded.foot}
        />
        {eng.rewards_claimed && (
          <KPI
            label={eng.rewards_claimed.label ?? 'Rewards claimed'}
            value={eng.rewards_claimed.display}
            category="Engagement"
            accent="engage"
            foot={eng.rewards_claimed.foot}
          />
        )}
      </div>

      {/* Per-brand performance — rendered here when DashboardConfig.segmentsLocation = 'overview' (Brazil + France).
          Spain omits segmentsLocation entirely, so the table doesn't appear there. */}
      {segments && segments.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <SegmentTable segments={segments} brandColors={brandColors} hide={hide} />
        </div>
      )}
    </div>
  );
}
